import * as React from 'react'

import { clsx } from 'clsx'

import {
  XIcon,
  CriticalIcon,
  Flex,
  GifIcon,
  IconButton,
  InfoIcon,
  VisuallyHidden,
  PdfIcon,
  PlayFilledIcon,
  Tooltip,
  UnstyledButton,
  WarningIcon,
  LoaderCircleIcon,
} from '@buffer-mono/popcorn'
import type {
  IconButtonProps,
  ImageProps,
  UnstyledButtonProps,
} from '@buffer-mono/popcorn'

import { MediaThumbnail } from '~publish/components/MediaThumbnail'

import styles from './AssetThumbnail.module.css'

import type { JSX } from 'react'

type FeedbackType = 'info' | 'warning' | 'critical'

type RootProps = React.ComponentProps<'div'>

/**
 * AssetThumbnail is a compound component for displaying media thumbnails with
 * interactive actions, indicators, and feedback icons.
 *
 * Actions are hidden by default and revealed on hover/focus for a clean UI.
 *
 * @example
 * // Basic image thumbnail with close button
 * <AssetThumbnail>
 *   <AssetThumbnail.Image src="image.jpg" alt="Photo" />
 *   <AssetThumbnail.Close onClick={handleRemove} />
 * </AssetThumbnail>
 *
 * @example
 * // Video thumbnail with indicator and actions
 * <AssetThumbnail>
 *   <AssetThumbnail.Clickable onClick={handlePreview}>
 *     <AssetThumbnail.Image src="video-thumb.jpg" alt="Video" />
 *     <AssetThumbnail.Indicator type="video" />
 *   </AssetThumbnail.Clickable>
 *   <AssetThumbnail.Actions>
 *     <AssetThumbnail.Action label="Edit" onClick={handleEdit}>
 *       <PencilIcon />
 *     </AssetThumbnail.Action>
 *   </AssetThumbnail.Actions>
 *   <AssetThumbnail.Close onClick={handleRemove} />
 * </AssetThumbnail>
 */
const Root = ({ className, ...props }: RootProps): JSX.Element => (
  <div className={clsx(styles.thumbnailContainer, className)} {...props} />
)

Root.displayName = 'AssetThumbnail'

type ThumbnailImageProps = ImageProps

/**
 * Renders the thumbnail image with consistent styling.
 * Wraps MediaThumbnail with proper border-radius and object-fit.
 *
 * @example
 * <AssetThumbnail.Image src="photo.jpg" alt="Uploaded photo" />
 */
const ThumbnailImage = React.forwardRef<HTMLImageElement, ThumbnailImageProps>(
  (props, ref) => <MediaThumbnail ref={ref} {...props} />,
)

ThumbnailImage.displayName = 'AssetThumbnail.Image'

type ClickableProps = UnstyledButtonProps

/**
 * Makes the thumbnail clickable with an inset border overlay.
 * Use this to wrap the Image when the thumbnail should open a preview dialog.
 *
 * @example
 * <AssetThumbnail.Clickable onClick={openPreview} aria-label="Open preview">
 *   <AssetThumbnail.Image src="photo.jpg" alt="Photo" />
 * </AssetThumbnail.Clickable>
 */
const Clickable = React.forwardRef<HTMLButtonElement, ClickableProps>(
  ({ className, ...props }, ref) => (
    <UnstyledButton
      ref={ref}
      className={clsx(styles.clickable, className)}
      {...props}
    />
  ),
)

Clickable.displayName = 'AssetThumbnail.Clickable'

type IndicatorType = 'video' | 'document' | 'gif'
type IndicatorSide = 'top' | 'right' | 'bottom' | 'left'
type IndicatorAlign = 'start' | 'center' | 'end'

type IndicatorProps = React.ComponentProps<'div'> & {
  type: IndicatorType
  side?: IndicatorSide
  align?: IndicatorAlign
  className?: string
}

const INDICATOR_CONTENT: Record<IndicatorType, React.ReactNode> = {
  video: <PlayFilledIcon size="small" />,
  document: <PdfIcon size="small" />,
  gif: <GifIcon size="small" />,
}

/**
 * Displays a visual indicator overlay for the media type (video, GIF, or document).
 * Positioned in the center by default, but can be placed at any corner using
 * `side` and `align` props.
 *
 * This is decorative (aria-hidden) since the media type should be conveyed
 * through the image alt text.
 *
 * @example
 * // Centered indicator (default)
 * <AssetThumbnail.Indicator type="video" />
 *
 * @example
 * // Bottom-right corner indicator
 * <AssetThumbnail.Indicator type="gif" side="bottom" align="end" />
 */
const Indicator = ({
  type,
  side,
  align = 'center',
  className,
  ...props
}: IndicatorProps): JSX.Element => (
  <div
    className={clsx(styles.indicator, className)}
    data-side={side}
    data-align={align}
    aria-hidden
    {...props}
  >
    {INDICATOR_CONTENT[type]}
  </div>
)

Indicator.displayName = 'AssetThumbnail.Indicator'

type OverlayProps = React.ComponentProps<'div'>

/**
 * A full-size overlay that covers the thumbnail.
 * Use for loading states or other blocking overlays.
 *
 * @example
 * <AssetThumbnail.Overlay>
 *   <AssetThumbnail.Loading label="Saving image" />
 * </AssetThumbnail.Overlay>
 */
const Overlay = ({ className, ...props }: OverlayProps): JSX.Element => (
  <div className={clsx(styles.overlay, className)} {...props} />
)

Overlay.displayName = 'AssetThumbnail.Overlay'

type LoadingProps = Omit<React.ComponentProps<'div'>, 'children'> & {
  label: string
}

/**
 * A centered spinner indicator for loading states.
 * Includes a visually hidden label for accessibility.
 *
 * @example
 * <AssetThumbnail.Loading label="Saving image" />
 */
const Loading = ({ label, className, ...props }: LoadingProps): JSX.Element => (
  // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- The thumbnail loader is a live status region with a visually hidden label.
  <div className={clsx(styles.loading, className)} role="status" {...props}>
    <VisuallyHidden>{label}</VisuallyHidden>
    <LoaderCircleIcon size="medium" />
  </div>
)

Loading.displayName = 'AssetThumbnail.Loading'

type ActionsProps = React.ComponentProps<'div'>

/**
 * Container for action buttons that appears at the bottom of the thumbnail.
 * Hidden by default and revealed on hover/focus with a smooth transition.
 *
 * @example
 * <AssetThumbnail.Actions>
 *   <AssetThumbnail.Action label="Edit" onClick={handleEdit}>
 *     <PencilIcon />
 *   </AssetThumbnail.Action>
 *   <AssetThumbnail.Action label="Tag" onClick={handleTag}>
 *     <TagIcon />
 *   </AssetThumbnail.Action>
 * </AssetThumbnail.Actions>
 */
const Actions = ({ className, ...props }: ActionsProps): JSX.Element => (
  <div className={clsx(styles.actions, className)} {...props} />
)

Actions.displayName = 'AssetThumbnail.Actions'

type ActionProps = Omit<IconButtonProps, 'variant'>

/**
 * An icon button action for the thumbnail. Use inside Actions for grouped
 * actions, or standalone for a single action (e.g., close button).
 *
 * When used standalone (outside Actions), it positions itself at the top-right
 * and is hidden until hover/focus.
 *
 * @example
 * <AssetThumbnail.Action label="Edit image" tooltip="Edit" onClick={handleEdit}>
 *   <PencilIcon />
 * </AssetThumbnail.Action>
 */
const Action = React.forwardRef<HTMLButtonElement, ActionProps>(
  ({ className, size = 'small', ...props }, ref) => (
    <IconButton
      ref={ref}
      variant="secondary"
      size={size}
      className={clsx(styles.action, className)}
      {...props}
    />
  ),
)

Action.displayName = 'AssetThumbnail.Action'

type CloseProps = Omit<IconButtonProps, 'children'>

/**
 * A close/remove button positioned at the top-right of the thumbnail.
 * Pre-configured with XIcon and "Remove" label.
 *
 * @example
 * <AssetThumbnail.Close
 *   onClick={handleRemove}
 *   label="Remove image"
 *   tooltip="Remove image"
 * />
 */
const Close = React.forwardRef<HTMLButtonElement, CloseProps>(
  ({ label = 'Remove', size = 'small', ...props }, ref) => (
    <Action ref={ref} label={label} size={size} {...props}>
      <XIcon />
    </Action>
  ),
)

Close.displayName = 'AssetThumbnail.Close'

/**
 * Displays a feedback icon (info, warning, or critical) at the top-left of the thumbnail.
 * Shows a tooltip with the provided content on hover.
 *
 * - `info`: Blue info icon, hidden until hover
 * - `warning`: Yellow warning icon, always visible
 * - `critical`: Red critical icon, hidden until hover
 *
 * @example
 * // Tip for users (info type, default)
 * <AssetThumbnail>
 *   <AssetThumbnail.Image src="photo.jpg" alt="Photo" />
 *   <AssetThumbnail.FeedbackIcon content="4:5 aspect ratio works best for Instagram" />
 * </AssetThumbnail>
 *
 * @example
 * // Validation warning (always visible)
 * <AssetThumbnail>
 *   <AssetThumbnail.Image src="photo.jpg" alt="Photo" />
 *   <AssetThumbnail.FeedbackIcon type="warning" content="Image exceeds size limit" />
 * </AssetThumbnail>
 */
type FeedbackIconElement = React.ElementRef<typeof Flex>
type FeedbackIconProps = Omit<
  React.ComponentPropsWithoutRef<typeof Flex>,
  'className' | 'children'
> & {
  /**
   * Tooltip content for the feedback icon
   */
  content: React.ReactNode
  /**
   * The feedback type
   */
  type?: FeedbackType
  className?: string
}

const FeedbackIcon = React.forwardRef<FeedbackIconElement, FeedbackIconProps>(
  ({ className, content, type = 'info', ...props }, forwardedRef) => {
    return (
      <Tooltip content={content}>
        <Flex
          {...props}
          ref={forwardedRef}
          data-type={type}
          className={clsx(styles.feedbackIcon, className)}
        >
          {type === 'info' && <InfoIcon size="small" />}
          {type === 'warning' && <WarningIcon size="small" />}
          {type === 'critical' && <CriticalIcon size="small" />}
        </Flex>
      </Tooltip>
    )
  },
)

FeedbackIcon.displayName = 'AssetThumbnail.FeedbackIcon'

export const assetThumbnailImageCenteredPlaceholderClass =
  styles.imageCenteredPlaceholder

export const AssetThumbnail = Object.assign(Root, {
  Action,
  Actions,
  Clickable,
  Close,
  FeedbackIcon,
  Image: ThumbnailImage,
  Indicator,
  Loading,
  Overlay,
})

export type {
  RootProps as AssetThumbnailProps,
  ActionProps as AssetThumbnailActionProps,
  ClickableProps as AssetThumbnailClickableProps,
  ActionsProps as AssetThumbnailActionsProps,
  CloseProps as AssetThumbnailCloseProps,
  ThumbnailImageProps as AssetThumbnailImageProps,
  IndicatorProps as AssetThumbnailIndicatorProps,
  FeedbackIconProps as AssetThumbnailFeedbackIconProps,
  LoadingProps as AssetThumbnailLoadingProps,
  OverlayProps as AssetThumbnailOverlayProps,
}
